import { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { varAlpha, mergeClasses } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { Image } from '../image';
import { Iconify } from '../iconify';
import { uploadClasses } from './classes';
import { RejectionFiles } from './components/rejection-files';

import type { UploadAvatarProps } from './types';

// ----------------------------------------------------------------------

const DEFAULT_DYNAMIC_PREVIEW = {
  height: 144,
  maxWidth: 220,
};

export function UploadAvatar({
  sx,
  error,
  value,
  disabled,
  helperText,
  className,
  onDelete,
  slotProps,
  dynamicPreview,
  ...other
}: UploadAvatarProps) {
  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    multiple: false,
    disabled,
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'image/jpg': [],
      '.jpeg': [],
      '.jpg': [],
      '.png': [],
    },
    ...other,
  });

  const hasFile = !!value;

  const hasError = isDragReject || !!error;

  const [preview, setPreview] = useState('');
  const [ratio, setRatio] = useState(1);

  const isDynamicPreview = !!dynamicPreview;
  const dynamicPreviewOptions = isDynamicPreview
    ? {
        ...DEFAULT_DYNAMIC_PREVIEW,
        ...(typeof dynamicPreview === 'object' ? dynamicPreview : {}),
      }
    : null;

  useEffect(() => {
    if (typeof value === 'string') {
      setPreview(value);
    } else if (value instanceof File) {
      setPreview(URL.createObjectURL(value));
    } else {
      setPreview('');
    }
  }, [value]);

  useEffect(() => {
    if (!isDynamicPreview || !preview) {
      setRatio(1);
      return undefined;
    }

    const img = new window.Image();
    img.onload = () => {
      setRatio(img.naturalWidth / img.naturalHeight);
    };
    img.src = preview;

    return () => {
      img.onload = null;
    };
  }, [isDynamicPreview, preview]);

  const rootSize = isDynamicPreview && dynamicPreviewOptions
    ? {
        width: hasFile
          ? Math.min(dynamicPreviewOptions.height * ratio, dynamicPreviewOptions.maxWidth)
          : dynamicPreviewOptions.height,
        height: dynamicPreviewOptions.height,
      }
    : {
        width: 144,
        height: 144,
      };

  const renderPreview = () =>
    hasFile && (
      <Image
        alt="Avatar"
        src={preview}
        sx={[
          {
            width: 1,
            height: 1,
            borderRadius: 1,
            ...(isDynamicPreview && {
              '& img': { objectFit: 'contain' },
            }),
          },
          ...(Array.isArray(slotProps?.preview) ? slotProps.preview : [slotProps?.preview]),
        ]}
      />
    );

  const renderPlaceholder = () => (
    <Box
      className="upload-placeholder"
      sx={(theme) => ({
        top: 0,
        gap: 1,
        left: 0,
        width: 1,
        height: 1,
        zIndex: 9,
        display: 'flex',
        borderRadius: 1,
        position: 'absolute',
        alignItems: 'center',
        color: 'text.disabled',
        flexDirection: 'column',
        justifyContent: 'center',
        bgcolor: varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
        transition: theme.transitions.create(['opacity'], {
          duration: theme.transitions.duration.shorter,
        }),
        '&:hover': { opacity: 0.72 },
        ...(hasError && {
          color: 'error.main',
          bgcolor: varAlpha(theme.vars.palette.error.mainChannel, 0.08),
        }),
        ...(hasFile && {
          zIndex: 9,
          opacity: 0,
          color: 'common.white',
          bgcolor: varAlpha(theme.vars.palette.grey['900Channel'], 0.64),
        }),
      })}
    >
      <Iconify icon="solar:camera-add-bold" width={32} />

      <Typography variant="caption">{hasFile ? 'Update photo' : 'Upload photo'}</Typography>

      {hasFile && (
        <Typography
          variant="caption"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.();
          }}
        >
          Remove
        </Typography>
      )}
    </Box>
  );

  const renderContent = () => (
    <Box
      sx={[
        {
          width: 1,
          height: 1,
          overflow: 'hidden',
          borderRadius: 1,
          position: 'relative',
        },
        ...(Array.isArray(slotProps?.content) ? slotProps.content : [slotProps?.content]),
      ]}
    >
      {renderPreview()}
      {renderPlaceholder()}
    </Box>
  );

  return (
    <>
      <Box
        {...getRootProps()}
        className={mergeClasses([uploadClasses.uploadBox, className])}
        sx={[
          (theme) => ({
            p: 1,
            width: rootSize.width,
            height: rootSize.height,
            cursor: 'pointer',
            overflow: 'hidden',
            borderRadius: 1,
            border: `1px dashed ${varAlpha(theme.vars.palette.grey['500Channel'], 0.2)}`,
            ...(isDragActive && { opacity: 0.72 }),
            ...(disabled && { opacity: 0.48, pointerEvents: 'none' }),
            ...(hasError && { borderColor: 'error.main' }),
            ...(hasFile && {
              ...(hasError && { bgcolor: varAlpha(theme.vars.palette.error.mainChannel, 0.08) }),
              '&:hover .upload-placeholder': { opacity: 1 },
            }),
          }),
          ...(Array.isArray(slotProps?.root) ? slotProps.root : [slotProps?.root]),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <input {...getInputProps()} />

        {renderContent()}
      </Box>

      {helperText && helperText}

      {!!fileRejections.length && <RejectionFiles files={fileRejections} />}
    </>
  );
}
